import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { TranslateService } from '@ngx-translate/core';

// Status options
export const HISTORY_STATUS_OPTIONS = [
  { label: 'e_invoice.history.status.issued', value: '1' },
  { label: 'e_invoice.history.status.replaced', value: '2' },
  { label: 'e_invoice.history.status.adjusted', value: '3' },
];

// Tax authority submission status options
export const HISTORY_TAX_SUBMISSION_OPTIONS = [
  { label: 'e_invoice.history.tax_submission.submitted', value: 'SUBMITTED' },
  { label: 'e_invoice.history.tax_submission.pending', value: 'PENDING' },
  { label: 'e_invoice.history.tax_submission.failed', value: 'FAILED' },
];

// Search form config
export const HISTORY_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'invoiceNo',
    label: 'e_invoice.history.search.invoice_number',
    type: 'text',
    placeholder: 'e_invoice.history.search.enter_invoice_number',
  },
  {
    key: 'orderNo',
    label: 'e_invoice.history.search.order_number',
    type: 'text',
    placeholder: 'e_invoice.history.search.enter_order_number',
  },
  {
    key: 'issuedDate',
    label: 'e_invoice.history.search.issued_date',
    type: 'dateRange',
    range: { startKey: 'issuedFrom', endKey: 'issuedTo' },
  },
  {
    key: 'providerId',
    label: 'e_invoice.history.search.provider',
    type: 'select',
    options: [],
    placeholder: 'e_invoice.history.search.select_provider',
    showClear: true,
  },
  {
    key: 'status',
    label: 'e_invoice.history.search.status',
    type: 'select',
    options: HISTORY_STATUS_OPTIONS,
    placeholder: 'e_invoice.history.search.select_status',
    showClear: true,
  },
];

// Default columns (shown on initial load)
export const HISTORY_DEFAULT_COLUMNS: TableColumn[] = [
  {
    label: 'e_invoice.history.table.store',
    prop: 'storeName',
    width: 150,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.invoice_number',
    prop: 'invoiceNumber',
    width: 160,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.buyer_name',
    prop: 'buyerName',
    width: 180,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.buyer_tax_code',
    prop: 'buyerTaxCode',
    width: 140,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.total_amount',
    prop: 'totalAmount',
    width: 140,
    align: 'right',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.status',
    prop: 'status',
    width: 130,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.issued_date',
    prop: 'issuedDate',
    width: 160,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.provider',
    prop: 'providerName',
    width: 150,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.operations',
    prop: 'actions',
    width: 160,
    align: 'center',
    type: 'actions',
  },
];

// Flexible columns (toggle-able via column selector)
export const HISTORY_FLEXIBLE_COLUMNS: TableColumn[] = [
  {
    label: 'e_invoice.history.table.subtotal',
    prop: 'subtotal',
    width: 130,
    align: 'right',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.tax_amount',
    prop: 'taxAmount',
    width: 120,
    align: 'right',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.discount',
    prop: 'discount',
    width: 120,
    align: 'right',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.payment_method',
    prop: 'paymentMethod',
    width: 130,
    align: 'left',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.currency',
    prop: 'currency',
    width: 80,
    align: 'center',
  },
  {
    label: 'e_invoice.history.table.tax_submission_status',
    prop: 'taxSubmissionStatus',
    width: 160,
    align: 'center',
    type: 'custom',
  },
  {
    label: 'e_invoice.history.table.store',
    prop: 'storeName',
    width: 150,
    align: 'left',
  },
  {
    label: 'e_invoice.history.table.company',
    prop: 'companyName',
    width: 180,
    align: 'left',
  },
];

// Status helpers
export function getInvoiceStatusSeverity(
  status: string,
): 'success' | 'warning' | 'danger' | 'secondary' | 'info' {
  switch (status) {
    case 'ISSUED':
    case '1':
      return 'success';
    case 'REPLACED':
      return 'warning';
    case 'ADJUSTED':
      return 'info';
    default:
      return 'secondary';
  }
}

export function getInvoiceStatusDisplayText(
  status: string,
  translateService: TranslateService,
): string {
  switch (status) {
    case 'ISSUED':
    case '1':
      return translateService.instant('e_invoice.history.status.issued');
    case 'REPLACED':
      return translateService.instant('e_invoice.history.status.replaced');
    case 'ADJUSTED':
      return translateService.instant('e_invoice.history.status.adjusted');
    default:
      return status || '';
  }
}

export function getTaxSubmissionSeverity(
  status: string,
): 'success' | 'warning' | 'danger' | 'secondary' {
  switch (status) {
    case 'SUBMITTED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'FAILED':
      return 'danger';
    default:
      return 'secondary';
  }
}

export function getTaxSubmissionDisplayText(
  status: string,
  translateService: TranslateService,
): string {
  switch (status) {
    case 'SUBMITTED':
      return translateService.instant(
        'e_invoice.history.tax_submission.submitted',
      );
    case 'PENDING':
      return translateService.instant(
        'e_invoice.history.tax_submission.pending',
      );
    case 'FAILED':
      return translateService.instant(
        'e_invoice.history.tax_submission.failed',
      );
    default:
      return status || '';
  }
}

export function getPaymentMethodDisplayText(
  paymentMethod: string | null | undefined,
  translateService: TranslateService,
): string {
  const code = paymentMethod || '5';
  return translateService.instant(`e_invoice.history.payment_methods.${code}`);
}

export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '--';
  }
  return value.toLocaleString('vi-VN');
}
